import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { PlusIcon, XCloseIcon } from '@untitledui-icons/react/line'

import { Button } from '@/components/base/buttons'
import { Checkbox } from '@/components/base/checkbox/checkbox'
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from '@/components/ui/Modal'
import {
  useAssignWidgetTemplate,
  useUnassignWidgetTemplate,
  useWidgetOverview,
  type WidgetOverviewRow,
  type WidgetTemplate,
  type WidgetTemplateAssignment,
} from '@/hooks/api/useWidgets'

interface WidgetTemplateAssigneesProps {
  template: WidgetTemplate
}

/**
 * The assistants this template styles. Assignments save on confirm rather than
 * with the template — they are a property of the assistant, not of the styling
 * document being edited.
 */
export function WidgetTemplateAssignees({ template }: WidgetTemplateAssigneesProps) {
  const { t } = useTranslation()
  const base = 'resources.widgets.assignees'

  const { data: rows } = useWidgetOverview()
  const assignTemplate = useAssignWidgetTemplate()
  const unassignTemplate = useUnassignWidgetTemplate()

  const [isPickerOpen, setPickerOpen] = useState(false)
  const [selectedIds, setSelectedIds] = useState<number[]>([])

  const assistants = (rows ?? []).filter((row) => row.customer_id === template.customer_id)
  const assignedIds = template.assignments.map((assignment) => assignment.assistant_id)
  const liveIds = assistants
    .filter((row) => row.connection_state === 'live')
    .map((row) => row.assistant_id)
  const isEveryLiveSelected = liveIds.length > 0 && liveIds.every((id) => selectedIds.includes(id))

  const openPicker = () => {
    setSelectedIds(assignedIds)
    setPickerOpen(true)
  }

  const canSelect = (row: WidgetOverviewRow) =>
    row.connection_state === 'live' || assignedIds.includes(row.assistant_id)

  const toggle = (row: WidgetOverviewRow) => {
    if (!canSelect(row)) return

    setSelectedIds((current) => {
      if (current.includes(row.assistant_id)) {
        return current.filter((id) => id !== row.assistant_id)
      }

      return [...current, row.assistant_id]
    })
  }

  const handleConfirm = async () => {
    const toAssign = selectedIds.filter((id) => !assignedIds.includes(id))
    const toRemove = assignedIds.filter((id) => !selectedIds.includes(id))

    if (toAssign.some((id) => !liveIds.includes(id))) {
      toast.error(t(`${base}.needs_connection_toast`))
      return
    }

    try {
      for (const assistantId of toRemove) {
        await unassignTemplate.mutateAsync(assistantId)
      }
      for (const assistantId of toAssign) {
        await assignTemplate.mutateAsync({
          assistant_id: assistantId,
          template_id: template.id,
        })
      }
      setPickerOpen(false)
    } catch {
      toast.error(t(`${base}.assign_failed_toast`))
    }
  }

  const handleRemove = async (assignment: WidgetTemplateAssignment) => {
    try {
      await unassignTemplate.mutateAsync(assignment.assistant_id)
    } catch {
      toast.error(t(`${base}.remove_failed_toast`))
    }
  }

  const isSaving = assignTemplate.isPending || unassignTemplate.isPending

  return (
    <section className="flex flex-col gap-1">
      <h2 className="text-lg leading-7 font-semibold text-gray-900 dark:text-gray-100">
        {t(`${base}.title`)}
      </h2>
      <p className="max-w-[471px] text-[13px] leading-[18px] text-gray-600 dark:text-gray-400">
        {t(`${base}.description`)}
      </p>

      <button
        type="button"
        onClick={openPicker}
        className="mt-[9px] inline-flex h-[22px] w-fit items-center gap-0.5 rounded-full border border-dashed border-gray-300 bg-gray-100 pr-2 pl-1.5 text-xs font-medium text-gray-700 hover:border-gray-400 hover:text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:text-gray-100"
      >
        <PlusIcon className="size-3 shrink-0" />
        {t(`${base}.assign`)}
      </button>

      {template.assignments.length > 0 && (
        <div className="mt-[9px] flex flex-wrap items-center gap-[13px]">
          {template.assignments.map((assignment) => (
            <span
              key={assignment.assistant_id}
              className="inline-flex h-[22px] items-center gap-1 rounded-full border border-gray-300 bg-gray-100 pr-2 pl-1.5 text-xs font-medium text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300"
            >
              <button
                type="button"
                aria-label={t(`${base}.remove`, { venue: assignment.assistant_name })}
                onClick={() => void handleRemove(assignment)}
                className="flex text-gray-500 hover:text-gray-900 dark:hover:text-gray-100"
              >
                <XCloseIcon className="size-3" />
              </button>
              {assignment.assistant_name}
            </span>
          ))}
        </div>
      )}

      <Modal isOpen={isPickerOpen} onOpenChange={setPickerOpen} size="lg">
        <ModalContent className="flex max-h-[calc(100vh-2rem)] flex-col">
          <ModalHeader
            onClose={() => setPickerOpen(false)}
            bordered={false}
            className="shrink-0 px-6 pt-6 pb-0"
          >
            <div>
              <ModalTitle>{t(`${base}.picker_title`)}</ModalTitle>
              <p className="mt-1 max-w-[430px] text-[13px] leading-[18px] text-gray-600 dark:text-gray-400">
                {t(`${base}.description`)}
              </p>
            </div>
          </ModalHeader>

          <ModalBody className="min-h-0 flex-1 px-6 pt-5 pb-0">
            {assistants.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t(`${base}.picker_empty`)}
              </p>
            ) : (
              <div
                role="group"
                aria-label={t(`${base}.picker_title`)}
                className="flex max-h-[min(20rem,calc(100vh-18rem))] flex-col gap-4 overflow-y-auto overscroll-contain"
              >
                {liveIds.length > 0 && (
                  <Checkbox
                    className="w-full gap-3"
                    isSelected={isEveryLiveSelected}
                    onChange={() => setSelectedIds(isEveryLiveSelected ? [] : liveIds)}
                    label={
                      <span className="text-gray-900 dark:text-gray-100">
                        {t(`${base}.select_all`)}
                      </span>
                    }
                  />
                )}

                {assistants.map((row) => {
                  const usesThisTemplate = row.template_id === template.id
                  const selectable = canSelect(row)
                  const hint = !selectable
                    ? t(`${base}.needs_connection`)
                    : usesThisTemplate
                      ? t(`${base}.uses_template`, { template: template.name })
                      : row.template_name
                        ? t(`${base}.uses_template`, { template: row.template_name })
                        : t(`${base}.no_template`)

                  return (
                    <Checkbox
                      key={row.assistant_id}
                      className="w-full gap-3"
                      isSelected={selectedIds.includes(row.assistant_id)}
                      isDisabled={!selectable}
                      onChange={() => toggle(row)}
                      label={
                        <span className="text-gray-900 dark:text-gray-100">
                          {row.assistant_name}
                        </span>
                      }
                      hint={
                        <span className="text-[13px] leading-[18px] text-gray-600 dark:text-gray-400">
                          {hint}
                        </span>
                      }
                    />
                  )
                })}
              </div>
            )}
          </ModalBody>

          <ModalFooter bordered={false} align="stretch" className="shrink-0 px-6 pt-6 pb-6">
            <Button color="secondary" size="lg" onPress={() => setPickerOpen(false)}>
              {t('resources.generic.cancel')}
            </Button>
            <Button
              color="primary"
              size="lg"
              isLoading={isSaving}
              onPress={() => void handleConfirm()}
            >
              {t(`${base}.confirm`)}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </section>
  )
}
